import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

/* Aceternity simple-navbar-with-hover-effects, adapted for BookingFlows:
   floating white pill, sticky at the top, hover-pill links, EN/AR toggle,
   Log in + Start free CTAs. */
export const FloatingNavbar = ({ isAr, switchLang }) => {
  const navigate = useNavigate();
  const navItems = [
    { name: isAr ? "استكشف التجارب" : "Explore experiences", action: () => navigate("/marketplace"), testid: "supplier-nav-explore" },
    { name: isAr ? "الميزات" : "Features", action: () => navigate("/features"), testid: "supplier-nav-features" },
    { name: isAr ? "المدوّنة" : "Blog", action: () => navigate("/blog"), testid: "supplier-nav-blog" },
    { name: isAr ? "الفريق" : "Meet the team", action: () => navigate("/meet-the-team"), testid: "supplier-nav-team" },
  ];

  return (
    <header className="sticky top-0 z-50 px-3 sm:px-4 pt-3 pointer-events-none" data-testid="floating-navbar">
      <DesktopNav navItems={navItems} isAr={isAr} switchLang={switchLang} navigate={navigate} />
      <MobileNav navItems={navItems} isAr={isAr} switchLang={switchLang} navigate={navigate} />
    </header>
  );
};

const LangToggle = ({ isAr, switchLang }) => (
  <div className="relative grid grid-cols-2 items-center rounded-full bg-gray-100 p-0.5 text-[11px] font-semibold shrink-0" data-testid="lang-toggle">
    <span
      aria-hidden="true"
      className={`absolute inset-y-0.5 left-0.5 w-[calc(50%-2px)] rounded-full bg-blue-600 transition-transform duration-300 ease-out ${isAr ? "translate-x-full" : ""}`}
    />
    <button
      onClick={() => switchLang("en")}
      data-testid="lang-toggle-en"
      className={`relative z-10 px-2.5 py-1 rounded-full transition-colors duration-300 ${!isAr ? "text-white" : "text-gray-500 hover:text-gray-800"}`}
    >EN</button>
    <button
      onClick={() => switchLang("ar")}
      data-testid="lang-toggle-ar"
      className={`relative z-10 px-2.5 py-1 rounded-full transition-colors duration-300 ${isAr ? "text-white" : "text-gray-500 hover:text-gray-800"}`}
    >عربي</button>
  </div>
);

const Logo = ({ navigate }) => (
  <button onClick={() => navigate("/")} className="relative z-20 flex items-center gap-2 px-1 py-1" data-testid="navbar-logo">
    <img src="/logo-icon.png" alt="BookingFlows" className="w-8 h-8 rounded-lg object-cover" />
    <span className="font-semibold text-gray-900 text-[15px] tracking-tight">BookingFlows</span>
  </button>
);

const DesktopNav = ({ navItems, isAr, switchLang, navigate }) => {
  const [hovered, setHovered] = useState(null);
  return (
    <motion.div
      onMouseLeave={() => setHovered(null)}
      className="pointer-events-auto relative z-[60] mx-auto hidden w-full max-w-6xl flex-row items-center justify-between rounded-full bg-white/[0.97] backdrop-blur-xl px-3 py-2 shadow-[0_10px_32px_rgba(2,12,27,0.07)] lg:flex"
    >
      <Logo navigate={navigate} />
      <div className="hidden flex-1 flex-row items-center justify-center text-sm font-medium text-gray-600 lg:flex">
        {navItems.map((navItem, idx) => (
          <button
            onMouseEnter={() => setHovered(idx)}
            onClick={navItem.action}
            className="relative px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
            key={`nav-${idx}`}
            data-testid={navItem.testid}
          >
            {hovered === idx && (
              <motion.div layoutId="bf-nav-hover" className="absolute inset-0 h-full w-full rounded-full bg-gray-100" />
            )}
            <span className="relative z-20">{navItem.name}</span>
          </button>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <LangToggle isAr={isAr} switchLang={switchLang} />
        <button
          onClick={() => navigate("/login")}
          data-testid="supplier-nav-auth-login"
          className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors px-2 py-2"
        >
          {isAr ? "تسجيل الدخول" : "Log in"}
        </button>
        <button
          onClick={() => navigate("/signup")}
          data-testid="supplier-nav-auth-signup"
          className="rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
        >
          {isAr ? "ابدأ مجاناً" : "Start free"}
        </button>
      </div>
    </motion.div>
  );
};

const MobileNav = ({ navItems, isAr, switchLang, navigate }) => {
  const [open, setOpen] = useState(false);
  const go = (action) => { setOpen(false); action(); };
  return (
    <motion.div
      animate={{ borderRadius: open ? "20px" : "2rem" }}
      className="pointer-events-auto relative mx-auto flex w-full max-w-[calc(100vw-1.5rem)] flex-col items-center justify-between bg-white/[0.97] backdrop-blur-xl px-3 py-2 shadow-[0_10px_32px_rgba(2,12,27,0.07)] lg:hidden"
    >
      <div className="flex w-full flex-row items-center justify-between">
        <Logo navigate={navigate} />
        <div className="flex items-center gap-2">
          <LangToggle isAr={isAr} switchLang={switchLang} />
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" className="p-1.5 rounded-full hover:bg-gray-100 transition-colors">
            {open ? <X className="w-5 h-5 text-gray-700" /> : <Menu className="w-5 h-5 text-gray-700" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-x-0 top-14 z-20 flex w-full flex-col items-start gap-1 rounded-2xl bg-white px-4 py-5 shadow-xl"
            dir={isAr ? "rtl" : undefined}
          >
            {navItems.map((navItem, idx) => (
              <button
                key={`mnav-${idx}`}
                onClick={() => go(navItem.action)}
                className="w-full text-left rtl:text-right px-2 py-2.5 text-[15px] text-gray-700 hover:text-gray-900 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid={`mobile-${navItem.testid}`}
              >
                {navItem.name}
              </button>
            ))}
            <div className="w-full h-px bg-gray-100 my-2" />
            <button
              onClick={() => go(() => navigate("/login"))}
              className="w-full text-left rtl:text-right px-2 py-2.5 text-[15px] font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="mobile-menu-login"
            >
              {isAr ? "تسجيل الدخول" : "Log in"}
            </button>
            <button
              onClick={() => go(() => navigate("/signup"))}
              className="mt-1 w-full rounded-full bg-blue-600 px-6 py-3 text-center text-[15px] font-semibold text-white hover:bg-blue-700 transition-colors"
              data-testid="mobile-menu-signup"
            >
              {isAr ? "ابدأ مجاناً" : "Start free"}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FloatingNavbar;
